/**
 * A caseworker's unsaved edit to an AI section, kept so their typing is never
 * lost (#281 follow-up): when the section changes under them (the server's
 * 409 `ai_fill_unit_changed`), on a browser reload, or on Cancel → Edit.
 *
 * Field keys are the edit box keys: a token's key, or `${listKey}|${rowId}|${col}`
 * for a list cell (`rowId` from `rowEditId`).
 */

type Lists = Record<string, Record<string, string>[]>;

export const UNIT_CHANGED_CODE = 'ai_fill_unit_changed';

/** What was typed, and what each typed field read when the editor opened. */
export interface PendingEdit {
  /** `hash` of the section the edit was made against. */
  hash?: string;
  /** Who typed it and when (stored drafts only): another user's, or one older than DRAFT_TTL_MS, is ignored. */
  userId?: string;
  savedAt?: number;
  tokens: Record<string, string>;
  tokenOriginals: Record<string, string>;
  rows: Record<string, string>;
  rowOriginals: Record<string, string>;
}

/** A typed field whose text changed underneath it: both are kept, nothing is lost. */
export interface DraftConflict {
  key: string;
  typed: string;
  /** What the field reads now; null when the field (a removed row) no longer exists. */
  latest: string | null;
}

export const isEmptyEdit = (p: PendingEdit) => Object.keys(p.tokens).length === 0 && Object.keys(p.rows).length === 0;

const rowIdOf = (row: Record<string, string>, index: number) => (typeof row.num === 'string' && row.num !== '' ? `n:${row.num}` : `p:${index + 1}`);

function cellOf(lists: Lists, key: string): string | undefined {
  const [listKey, id, col] = key.split('|');
  const row = (lists[listKey] ?? []).find((r, i) => rowIdOf(r, i) === id);
  return row?.[col];
}

/**
 * Re-applies a pending edit onto the section's latest text. A typed field
 * whose original still reads the same is re-applied as typed; one whose text
 * changed (or that no longer exists) keeps the LATEST text in the box and is
 * returned as a conflict, so the caseworker sees both and chooses.
 */
export function rebaseEdit(pending: PendingEdit, latest: { tokens: Record<string, string>; lists: Lists }) {
  const draft: Record<string, string> = { ...latest.tokens };
  const rowDraft: Record<string, string> = {};
  const conflicts: DraftConflict[] = [];
  for (const [k, typed] of Object.entries(pending.tokens)) {
    const now = latest.tokens[k];
    if (now !== undefined && now === pending.tokenOriginals[k]) draft[k] = typed;
    else if (now !== typed) conflicts.push({ key: k, typed, latest: now ?? null });
  }
  for (const [k, typed] of Object.entries(pending.rows)) {
    const now = cellOf(latest.lists, k);
    if (now !== undefined && now === pending.rowOriginals[k]) rowDraft[k] = typed;
    else if (now !== typed) conflicts.push({ key: k, typed, latest: now ?? null });
  }
  return { draft, rowDraft, conflicts };
}

// ── sessionStorage (per viewer convenience; every access may throw) ─────────

/** Every stored draft key starts with this (cleared on explicit sign-out and idle timeout — `lib/auth/provider.tsx`; a session expiry keeps them). */
export const AI_FILL_DRAFT_PREFIX = 'ai-fill-draft:';
const PREFIX = AI_FILL_DRAFT_PREFIX;
/** A stored draft older than this is dropped, not offered. */
export const DRAFT_TTL_MS = 12 * 60 * 60 * 1000;
const who = (userId: string | null | undefined) => userId || 'anon';
const unitPrefix = (userId: string | null | undefined, checkpointId: string, unitKey: string) => `${PREFIX}${who(userId)}:${checkpointId}:${unitKey}:`;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function keysFor(s: Storage, prefix: string): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k && k.startsWith(prefix)) out.push(k);
    }
  } catch {
    /* unreadable storage: nothing stored */
  }
  return out;
}

/** Drops every stored draft (all users, all sections) — explicit sign-out and idle timeout only. */
export function clearAllDrafts(): void {
  const s = storage();
  if (!s) return;
  for (const k of keysFor(s, PREFIX)) {
    try {
      s.removeItem(k);
    } catch {
      /* ignore */
    }
  }
}

/** Drops every stored draft that belongs to someone other than `userId` (at sign-in). */
export function clearOtherUsersDrafts(userId: string): void {
  const s = storage();
  if (!s) return;
  const mine = `${PREFIX}${who(userId)}:`;
  for (const k of keysFor(s, PREFIX)) {
    if (k.startsWith(mine)) continue;
    try {
      s.removeItem(k);
    } catch {
      /* ignore */
    }
  }
}

/** Drops every stored draft of this section (this user's). */
export function clearDraft(userId: string | null | undefined, checkpointId: string, unitKey: string): void {
  const s = storage();
  if (!s) return;
  for (const k of keysFor(s, unitPrefix(userId, checkpointId, unitKey))) {
    try {
      s.removeItem(k);
    } catch {
      /* ignore */
    }
  }
}

/** Stores the pending edit under user + checkpoint + unit + the hash it was made against (one per section). */
export function saveDraft(userId: string | null | undefined, checkpointId: string, unitKey: string, pending: PendingEdit, now: number = Date.now()): void {
  clearDraft(userId, checkpointId, unitKey);
  if (isEmptyEdit(pending)) return;
  const s = storage();
  if (!s) return;
  try {
    s.setItem(`${unitPrefix(userId, checkpointId, unitKey)}${pending.hash ?? '-'}`, JSON.stringify({ ...pending, userId: who(userId), savedAt: now }));
  } catch {
    /* quota / blocked: the in-memory draft still stands */
  }
}

const strMap = (v: unknown): Record<string, string> =>
  v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.entries(v as Record<string, unknown>).filter(([, x]) => typeof x === 'string')) as Record<string, string> : {};

/**
 * This user's stored unsaved edit of the section, if any (whatever hash it
 * was made against). Another user's draft, an expired one (DRAFT_TTL_MS) or
 * a malformed / tampered entry is never offered — and never throws.
 */
export function loadDraft(userId: string | null | undefined, checkpointId: string, unitKey: string, now: number = Date.now()): PendingEdit | null {
  const s = storage();
  if (!s) return null;
  for (const k of keysFor(s, unitPrefix(userId, checkpointId, unitKey))) {
    try {
      const v = JSON.parse(s.getItem(k) ?? 'null') as Record<string, unknown> | null;
      if (!v || typeof v !== 'object') continue;
      if (v.userId !== who(userId)) continue;
      if (typeof v.savedAt !== 'number' || now - v.savedAt > DRAFT_TTL_MS) {
        s.removeItem(k);
        continue;
      }
      const draft: PendingEdit = {
        ...(typeof v.hash === 'string' ? { hash: v.hash } : {}),
        userId: v.userId as string,
        savedAt: v.savedAt,
        tokens: strMap(v.tokens),
        tokenOriginals: strMap(v.tokenOriginals),
        rows: strMap(v.rows),
        rowOriginals: strMap(v.rowOriginals),
      };
      if (!isEmptyEdit(draft)) return draft;
    } catch {
      /* corrupt entry: ignore it */
    }
  }
  return null;
}
